import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CheckIn.css';
import { searchUserByEmail, searchUsersAutocomplete, recordCheckin } from './api';
import CheckInStats from './components/CheckInStats';
import CheckInRecentList from './components/CheckInRecentList';
import CheckInExport from './components/CheckInExport';

// Dashboard password from environment variable (reuse from Dashboard)
const DASHBOARD_PASSWORD = process.env.REACT_APP_DASHBOARD_PASSWORD || 'admin123';

// Debounce delay for autocomplete (ms)
const AUTOCOMPLETE_DELAY = 300;

/**
 * CheckIn Component
 *
 * Password-protected check-in interface for event staff to:
 * - Search for registered users by email or last name (with autocomplete)
 * - View user registration details
 * - Record check-in visits
 */
function CheckIn() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Check if already authenticated on mount
  useEffect(() => {
    const auth = localStorage.getItem('dashboard_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced autocomplete search
  const performAutocomplete = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchLoading(true);
    try {
      const result = await searchUsersAutocomplete(query);
      if (result.success && result.suggestions) {
        setSuggestions(result.suggestions);
        setShowSuggestions(result.suggestions.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error('Autocomplete error:', err);
      setSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setError(null);

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for autocomplete
    debounceTimerRef.current = setTimeout(() => {
      performAutocomplete(value);
    }, AUTOCOMPLETE_DELAY);
  };

  // Handle selecting a suggestion
  const handleSelectSuggestion = (selectedUser) => {
    setUser(selectedUser);
    setSearchQuery(`${selectedUser.firstname} ${selectedUser.lastname}`);
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
    setSuccess(null);
  };

  // Handle form submit (exact email search as fallback)
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setShowSuggestions(false);
    setSearchLoading(true);
    setError(null);
    setSuccess(null);
    setUser(null);

    try {
      // Try exact email search first
      const result = await searchUserByEmail(searchQuery.trim());
      if (result.success) {
        setUser(result.user);
      } else {
        // If not found by email, show autocomplete results
        const autocompleteResult = await searchUsersAutocomplete(searchQuery.trim());
        if (autocompleteResult.success && autocompleteResult.suggestions?.length > 0) {
          setSuggestions(autocompleteResult.suggestions);
          setShowSuggestions(true);
        } else {
          setError('No users found matching your search');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to search for user');
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === DASHBOARD_PASSWORD) {
      localStorage.setItem('dashboard_auth', 'true');
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dashboard_auth');
    setIsAuthenticated(false);
    setUser(null);
    setSearchQuery('');
    setSuggestions([]);
  };

  const handleCheckin = async () => {
    if (!user) return;

    setCheckinLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await recordCheckin(user.email);
      if (result.success) {
        setSuccess(`${user.firstname} has been checked in successfully!`);
        // Refresh user data to show updated visit count
        const refreshed = await searchUserByEmail(user.email);
        if (refreshed.success) {
          setUser(refreshed.user);
        }
        // Trigger refresh of stats and recent list
        setRefreshTrigger(prev => prev + 1);
      } else {
        if (result.error === 'ALREADY_CHECKED_IN_TODAY') {
          setError(`${user.firstname} has already checked in today.`);
        } else {
          setError(result.message || 'Failed to check in user');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to check in user');
    } finally {
      setCheckinLoading(false);
    }
  };

  const clearSearch = () => {
    setUser(null);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
    setSuccess(null);
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString();
  };

  // Password prompt screen
  if (!isAuthenticated) {
    return (
      <div className="checkin-login">
        <div className="checkin-login-card">
          <h1>Check-In</h1>
          <p>Enter password to continue</p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <div className="password-error">{passwordError}</div>}
            <button type="submit">Access Check-In</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin">
      <header className="checkin-header">
        <h1>Event Check-In</h1>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </header>

      <main className="checkin-main">
        {/* Stats Cards */}
        <CheckInStats refreshTrigger={refreshTrigger} />

        {/* Search Form */}
        <div className="search-card">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-container">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search by email or last name..."
                className="search-input"
                autoComplete="off"
              />
              {searchLoading && <div className="search-spinner" />}

              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-dropdown" ref={suggestionsRef}>
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <div className="suggestion-name">
                        {suggestion.firstname} {suggestion.lastname}
                      </div>
                      <div className="suggestion-email">{suggestion.email}</div>
                      {suggestion.visitCount > 0 && (
                        <div className="suggestion-visits">
                          {suggestion.visitCount} visit{suggestion.visitCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="search-button"
              disabled={searchLoading || !searchQuery.trim()}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
            {user && (
              <button
                type="button"
                onClick={clearSearch}
                className="clear-button"
              >
                Clear
              </button>
            )}
          </form>
          <p className="search-hint">Type at least 2 characters to search</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="checkin-error">
            <p>{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="checkin-success">
            <p>{success}</p>
          </div>
        )}

        {/* User Card */}
        {user && (
          <div className="user-card">
            <div className="user-info">
              <h2>{user.firstname} {user.lastname}</h2>
              <p className="user-email">{user.email}</p>

              <div className="user-details">
                <div className="detail-row">
                  <span className="detail-label">Country:</span>
                  <span className="detail-value">{user.country || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Registered:</span>
                  <span className="detail-value">{formatDate(user.registeredAt)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total Visits:</span>
                  <span className="detail-value visit-count">{user.visitCount || 0}</span>
                </div>
                {user.lastVisit && (
                  <div className="detail-row">
                    <span className="detail-label">Last Visit:</span>
                    <span className="detail-value">{formatDate(user.lastVisit)}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleCheckin}
              className="checkin-button"
              disabled={checkinLoading}
            >
              {checkinLoading ? 'Processing...' : 'Check-In'}
            </button>
          </div>
        )}

        {/* Recent Check-ins List */}
        <CheckInRecentList refreshTrigger={refreshTrigger} />

        {/* Export Section */}
        <CheckInExport />
      </main>
    </div>
  );
}

export default CheckIn;
